import React, { useEffect, useRef, useState } from "react";
import { postLeadsBulk } from "../api/leadsTableApi";

export const LIST_TYPES = ["List 1", "List 2", "List 3"];
export const STATUS_OPTS = ["Pending", "Interested", "Not Interested"];

export type AddLeadTarget = { district: string; mandal: string; village: string };

export function AddLeadModal({
  target,
  onClose,
  onSaved,
}: {
  target: AddLeadTarget;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [appNo, setAppNo]       = useState("");
  const [appName, setAppName]   = useState("");
  const [mobile, setMobile]     = useState("");
  const [aadhar, setAadhar]     = useState("");
  const [listType, setListType] = useState(() => LIST_TYPES[0]);
  const [status, setStatus]     = useState(STATUS_OPTS[0]);
  const [busy, setBusy]         = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const firstRef                = useRef<HTMLInputElement>(null);

  useEffect(() => { firstRef.current?.focus(); }, []);

  useEffect(() => {
    if (!LIST_TYPES.includes(listType)) setListType(LIST_TYPES[0]);
  }, [listType]);

  useEffect(() => {
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!appNo.trim()) { setError("Application No is required."); return; }
    if (!appName.trim()) { setError("Application Name is required."); return; }
    setBusy(true);
    try {
      const combinedStatus = [listType, status].filter(Boolean).join(" · ");
      await postLeadsBulk([{
        leadId:        appNo.trim(),
        district:      target.district,
        mandal:        target.mandal,
        village:       target.village,
        applicantName: appName.trim() || null,
        mobileNumber:  mobile.trim() || null,
        aadhaarNumber: aadhar.trim() || null,
        status:        combinedStatus,
      }]);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save lead.");
    } finally {
      setBusy(false);
    }
  }

  const fieldStyle: React.CSSProperties = {
    width: "100%", padding: "0.45rem 0.7rem",
    border: "1.5px solid #cbd5e1", borderRadius: "7px",
    fontSize: "0.875rem", outline: "none", background: "#fff", color: "#1e293b",
    boxSizing: "border-box",
  };
  const readonlyStyle: React.CSSProperties = { ...fieldStyle, background: "#f1f5f9", color: "#64748b" };
  const labelStyle: React.CSSProperties = { fontSize: "0.78rem", fontWeight: 600, color: "#374151", marginBottom: "4px", display: "block" };

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000, padding: "1rem",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#fff", borderRadius: "12px", width: "100%", maxWidth: "480px",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
      }}>
        {/* header */}
        <div style={{ background: "#1e3a5f", padding: "1rem 1.25rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ color: "#fff", fontWeight: 700, fontSize: "1rem" }}>Add Lead</div>
            <div style={{ color: "#93c5fd", fontSize: "0.75rem", marginTop: "2px" }}>
              {target.district} › {target.mandal} › {target.village}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#93c5fd", fontSize: "1.3rem", cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>

        {/* body */}
        <form onSubmit={(e) => void submit(e)} style={{ padding: "1.25rem", display: "flex", flexDirection: "column", gap: "0.9rem" }}>

          {/* pre-filled row */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.75rem" }}>
            {[["District", target.district], ["Mandal", target.mandal], ["Village", target.village]].map(([lbl, val]) => (
              <div key={lbl}>
                <label style={labelStyle}>{lbl}</label>
                <input value={val} readOnly style={readonlyStyle} />
              </div>
            ))}
          </div>

          {/* application no + name */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label style={labelStyle}>Application No <span style={{ color: "#ef4444" }}>*</span></label>
              <input ref={firstRef} value={appNo} onChange={(e) => setAppNo(e.target.value)} placeholder="e.g. 204852/1075289" style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Applicant Name <span style={{ color: "#ef4444" }}>*</span></label>
              <input value={appName} onChange={(e) => setAppName(e.target.value)} placeholder="Full name" style={fieldStyle} />
            </div>
          </div>

          {/* mobile + aadhar */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label style={labelStyle}>Mobile Number</label>
              <input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="10-digit number" maxLength={10} style={fieldStyle} />
            </div>
            <div>
              <label style={labelStyle}>Aadhar Number</label>
              <input value={aadhar} onChange={(e) => setAadhar(e.target.value)} placeholder="12-digit number" maxLength={12} style={fieldStyle} />
            </div>
          </div>

          {/* list type + status */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
            <div>
              <label style={labelStyle}>Initial List Type</label>
              <select
                value={LIST_TYPES.includes(listType) ? listType : LIST_TYPES[0]}
                onChange={(e) => setListType(e.target.value)}
                style={{ ...fieldStyle }}
              >
                {LIST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Application Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...fieldStyle }}>
                {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {error && (
            <div style={{ padding: "0.5rem 0.75rem", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "6px", color: "#b91c1c", fontSize: "0.8rem" }}>
              {error}
            </div>
          )}

          {/* actions */}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.6rem", marginTop: "0.25rem" }}>
            <button type="button" onClick={onClose} disabled={busy} style={{
              padding: "0.45rem 1.1rem", borderRadius: "7px", border: "1.5px solid #cbd5e1",
              background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer",
            }}>
              Cancel
            </button>
            <button type="submit" disabled={busy} style={{
              padding: "0.45rem 1.25rem", borderRadius: "7px", border: "none",
              background: busy ? "#93c5fd" : "#1d4ed8", color: "#fff",
              fontWeight: 700, fontSize: "0.875rem", cursor: busy ? "not-allowed" : "pointer",
            }}>
              {busy ? "Saving…" : "Save Lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
